import React, { useEffect, useRef, useState } from 'react';
import { GameResultOverlay, GameShell } from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';
import {
    cellKey,
    createMatrixRound,
} from './rule-matrix/matrixRound';
import { formatGameCopy } from './gameCopy';

const { normalizeBrainGameLevel } = brainGameLevels;

function MatrixToken({ value }) {
    if (!value) {
        return <span className="UFOV-brain-games__matrix-missing">?</span>;
    }

    if (value.kind === 'number') {
        return <span className="UFOV-brain-games__matrix-number">{value.value}</span>;
    }

    if (value.kind === 'positions') {
        const slotCount = value.gridSize * value.gridSize;
        return (
            <span
                className={`UFOV-brain-games__matrix-position-grid is-${value.gridSize}`}
                style={{ '--matrix-position-size': value.gridSize }}
            >
                {Array.from({ length: slotCount }, (_, index) => (
                    <i className={value.mask & (1 << index) ? 'is-active' : ''} key={index} />
                ))}
            </span>
        );
    }

    return (
        <span
            className={`UFOV-brain-games__matrix-token is-size-${value.size ?? 2}`}
            style={{ transform: `rotate(${value.rotation || 0}deg)` }}
        >
            {Array.from({ length: value.count || 1 }, (_, index) => (
                <i
                    className={`is-${value.shape} ${value.filled === false ? '' : 'is-filled'}`.trim()}
                    key={index}
                    style={{ '--matrix-tone': value.tone || '#315fcb' }}
                />
            ))}
        </span>
    );
}

function RuleMatrixGame({ copy = {}, initialLevel, ...props }) {
    const startingLevel = normalizeBrainGameLevel('rule-matrix', initialLevel);
    const timerRef = useRef(null);
    const runSeedRef = useRef(Date.now());
    const nextLevelRef = useRef(startingLevel);
    const [roundIndex, setRoundIndex] = useState(1);
    const [level, setLevel] = useState(startingLevel);
    const [round, setRound] = useState(() => createMatrixRound(1, runSeedRef.current, startingLevel));
    const [choice, setChoice] = useState('');
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const maxRounds = 15;
    const done = roundIndex > maxRounds;
    const correctKey = cellKey(round.correct);
    const correct = choice && choice === correctKey;

    useEffect(() => () => window.clearTimeout(timerRef.current), []);

    const reset = () => {
        window.clearTimeout(timerRef.current);
        runSeedRef.current = Date.now();
        nextLevelRef.current = startingLevel;
        setRoundIndex(1);
        setLevel(startingLevel);
        setRound(createMatrixRound(1, runSeedRef.current, startingLevel));
        setChoice('');
        setScore(0);
        setStreak(0);
        setBestStreak(0);
    };

    const choose = (value) => {
        if (choice || done) {
            return;
        }

        const key = cellKey(value);
        const isCorrect = key === correctKey;
        const nextStreak = isCorrect ? streak + 1 : 0;
        const shouldLevelUp = isCorrect && nextStreak > 0 && nextStreak % 2 === 0;
        nextLevelRef.current = normalizeBrainGameLevel(
            'rule-matrix',
            isCorrect ? level + (shouldLevelUp ? 1 : 0) : level - 1
        );
        setChoice(key);
        setScore((current) => current + (isCorrect ? 1 : 0));
        setStreak(nextStreak);
        setBestStreak((current) => Math.max(current, nextStreak));

        timerRef.current = window.setTimeout(() => {
            const nextRoundIndex = roundIndex + 1;
            setRoundIndex(nextRoundIndex);
            setChoice('');
            if (nextRoundIndex <= maxRounds) {
                const nextLevel = nextLevelRef.current;
                setLevel(nextLevel);
                setRound(createMatrixRound(nextRoundIndex, runSeedRef.current, nextLevel, round.family));
            }
        }, isCorrect ? 760 : 980);
    };

    const metrics = { level, maxScore: maxRounds, score, streak: bestStreak };

    return (
        <GameShell {...props} copy={copy} metrics={metrics} tone={correct || done ? 'good' : choice ? 'miss' : 'ready'}>
            <div className="UFOV-brain-games__rule-matrix-game">
                <div className="UFOV-brain-games__rule-matrix" aria-label={copy.ruleMatrixCue}>
                    {round.cells.map((value, index) => (
                        <div className={value ? '' : 'is-missing'} key={`${round.id}-${index}`}>
                            <MatrixToken value={value} />
                        </div>
                    ))}
                </div>
                <div className="UFOV-brain-games__game-cue">{copy.ruleMatrixCue}</div>
                <div className="UFOV-brain-games__matrix-options">
                    {round.options.map((value, index) => {
                        const key = cellKey(value);
                        return (
                            <button
                                aria-label={formatGameCopy(copy.answerOption, { number: index + 1 })}
                                className={`${choice === key ? 'is-picked' : ''} ${choice && key === correctKey ? 'is-correct' : ''}`.trim()}
                                disabled={Boolean(choice) || done}
                                key={key}
                                onClick={() => choose(value)}
                                type="button"
                            >
                                <MatrixToken value={value} />
                            </button>
                        );
                    })}
                </div>
            </div>
            {done ? <GameResultOverlay metrics={metrics} onRestart={reset} /> : null}
        </GameShell>
    );
}

export default RuleMatrixGame;
